/* eslint-disable autofix/no-unused-vars */
// <copyright file="CombinedDataInterface.tsx">
// © 2025 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable @typescript-eslint/no-explicit-any */

import type { MapNode, Maneuver } from "src/components/CvdfVisualizer/MapDataInterface";

// Define a type for LatLng tuple as it is used frequently
type LatLng = [number, number];

export interface IntersectionData {
    intersectionPoint: LatLng;        // Coordinates of the intersection
    lanes: LaneData[];                // Array of lanes in the intersection
    signalStates: Map<number, SignalState>;   // Map of signal groups to their current signal states
    mapJ2735Uper: string; // Map Uper
    spatJ2735Uper?: string; // SPaT Uper
    region: number | null;
    mapIngested? : string | null;
    spatIngested?: string | null;
}

export enum LaneTypeEnum {
  Vehicle = "laneAttributesVehicle",
  Crosswalk = "laneAttributesCrosswalk",
  Bike = "laneAttributesBike",
  Sidewalk = "laneAttributesSidewalk",
  Barrier = "laneAttributesBarrier",
  Striping = "laneAttributesStriping",
  TrackedVehicle = "laneAttributesTrackedVehicle",
  Parking = "laneAttributesParking"
}

export interface LaneData {
    laneId: number;                   // Unique identifier for the lane
    egressPath: boolean;              // Indicates if the lane is an egress path
    endPoint: LatLng;                 // Coordinates of the end point of the lane
    signalGroups: number[];           // List of signal groups associated with the lane
    nodeList: {
        nodes: MapNode[];
        computed?: any;
    };
    maneuvers: Maneuver | null;
    laneType: LaneTypeEnum | null;
}

export interface SignalState {
    signalGroup: number;              // Unique identifier for the signal group
    stateTimeSpeed: StateTimeSpeed[]; // Array of state time speed data
}

interface StateTimeSpeed {
    eventState: string;               // The current event state (e.g., 'stopAndRemain', 'permissiveMovementAllowed')
    timing: {
        startTime: number;            // Start time of the timing
        minEndTime: number;           // Minimum end time of the timing
        maxEndTime: number;           // Maximum end time of the timing
        likelyTime: number;           // Likely time for the timing
        confidence: number;           // Confidence level for the timing
        nextTime: number;             // Next time for timing
    };
}

// Define the data structure type using a Map where the key is the intersection ID
export type CombinedData = Map<number, IntersectionData>;
